import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/crmApi";
import { Loader } from "@/crm/ui";
import { toast } from "sonner";
import {
  ArrowLeft, Play, Pencil, Trash2, Building2, Loader2, Mail, Send,
  Target, CalendarClock, Filter, AlertTriangle, Archive, ArchiveRestore,
} from "lucide-react";
import { ancienneteLabel } from "@/crm/anciennete";
import CampaignForm from "@/crm/components/CampaignForm";

const STATUS_STYLES = {
  done: "bg-emerald-100 text-emerald-700",
  running: "bg-amber-100 text-amber-700",
  error: "bg-rose-100 text-rose-700",
  draft: "bg-slate-100 text-slate-500",
};

const STATUS_LABELS = {
  done: "Terminée", running: "En cours", error: "Erreur", draft: "Non lancée",
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => api.get(`/campaigns/${id}`)
    .then(({ data }) => setC(data))
    .catch(() => setNotFound(true)), [id]);

  useEffect(() => {
    load();
    // Rafraîchissement pendant l'extraction (mêmes 3 s que la liste).
    timer.current = setInterval(load, 3000);
    return () => clearInterval(timer.current);
  }, [load]);

  if (notFound) return <p className="text-sm text-slate-500">Campagne introuvable.</p>;
  if (!c) return <Loader />;

  const running = c.live?.status === "running";
  const pct = c.live?.objectif ? Math.min(100, Math.round((c.live.inserted / c.live.objectif) * 100)) : 0;

  const run = async () => {
    try { await api.post(`/campaigns/${id}/run`); toast.success("Extraction lancée"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const archive = async () => {
    try {
      await api.post(`/campaigns/${id}/archive`);
      toast.success(`Campagne archivée — suppression automatique dans ${c.retention_days} jours`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const unarchive = async () => {
    try { await api.post(`/campaigns/${id}/unarchive`); toast.success("Campagne restaurée"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    try { await api.delete(`/campaigns/${id}`); toast.success("Campagne supprimée"); navigate("/campagnes"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // Les critères affichés sont ceux de la source : NAF, état et ancienneté
  // sont des notions du registre, le type d'établissement une notion Google.
  const google = c.source === "google_places";
  const criteres = [
    ["Source", c.source_label || (google ? "Google Places" : "Registre officiel (SIRENE)")],
    ...(google
      ? [["Type d'établissement", c.google_type_label || c.google_type]]
      : [["Code NAF", c.naf], ["Section d'activité", c.section_activite]]),
    ["Département", c.departement],
    ["Codes postaux", c.codes_postaux?.length ? c.codes_postaux.join(", ") : null],
    ["Mot-clé", c.query],
    ...(google ? [] : [
      ["État administratif", c.etat_administratif === "C" ? "Cessées" : "Actives"],
      ["Ancienneté", c.anciennete_max_jours ? `Créées ${ancienneteLabel(c.anciennete_max_jours)}` : "Sans limite"],
    ]),
    ["Objectif", `${c.objectif} prospects`],
  ];

  const stats = c.stats || {};

  return (
    <div>
      <button onClick={() => navigate("/campagnes")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft size={16} /> Retour aux campagnes
      </button>

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading font-bold text-2xl text-slate-800">{c.name}</h1>
              <span className={`text-xs px-2 py-0.5 rounded-md ${STATUS_STYLES[c.status] || STATUS_STYLES.draft}`}
                data-testid="campaign-status">
                {STATUS_LABELS[c.status] || c.status}
              </span>
              {c.archived && (
                <span data-testid="campaign-archived" className="flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                  <Archive size={12} /> Archivée
                  {c.days_before_purge != null && ` · suppression dans ${c.days_before_purge} j`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
              <span className="flex items-center gap-1.5">
                <Building2 size={15} className="text-indigo-500" />
                <b className="font-mono">{c.companies_count}</b> entreprises importées
              </span>
              {c.last_run_at && (
                <span className="flex items-center gap-1.5 text-slate-400 text-xs">
                  <CalendarClock size={14} /> Dernière exécution : {new Date(c.last_run_at).toLocaleString("fr-FR")}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="detail-run" onClick={run} disabled={running}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-60">
              {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
              {running ? "En cours…" : "Lancer l'extraction"}
            </button>
            {c.status === "draft" && (
              <button data-testid="detail-edit" onClick={() => setEditing(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
                <Pencil size={15} /> Modifier
              </button>
            )}
            <button onClick={() => navigate(`/entreprises?campaign_id=${c.id}`)}
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
              Voir les entreprises
            </button>
            {c.archived ? (
              <button data-testid="detail-unarchive" onClick={unarchive}
                title="Restaurer — arrête le compte à rebours de suppression"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
                <ArchiveRestore size={15} /> Restaurer
              </button>
            ) : c.status !== "draft" && (
              <button data-testid="detail-archive" onClick={archive} title="Archiver la campagne"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
                <Archive size={15} /> Archiver
              </button>
            )}
            <button data-testid="detail-delete" onClick={del} title="Supprimer la campagne"
              className="px-3 py-2 rounded-lg border border-slate-300 text-slate-400 hover:text-rose-500"><Trash2 size={15} /></button>
          </div>
        </div>

        {running && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>{c.live.current}</span><span>{c.live.inserted} / {c.live.objectif}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Filter size={17} /> Critères d'extraction
          </h3>
          <p className="text-xs text-slate-400 mb-3">
            {c.status === "draft"
              ? "Modifiables tant que l'extraction n'a pas été lancée."
              : "Figés depuis la première exécution, pour rester cohérents avec les entreprises importées."}
          </p>
          <dl className="divide-y divide-slate-50">
            {criteres.map(([label, value]) => (
              <div key={label} className="flex items-baseline gap-3 py-2 text-sm">
                <dt className="text-slate-400 w-40 shrink-0">{label}</dt>
                <dd className={value ? "text-slate-700" : "text-slate-300"}>{value || "—"}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-heading font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Target size={17} /> Résultats de la dernière extraction
            </h3>
            {c.status === "draft" ? (
              <p className="text-sm text-slate-400">Aucune extraction lancée pour l'instant.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Prospects importés" value={stats.prospects} accent="text-emerald-600" />
                <Stat label="Fiches traitées" value={stats.processed} />
                <Stat label="Déjà connues (mises à jour)" value={stats.updated} />
                <Stat label={google ? "Écartées : hors zone ou fermées" : "Écartées : hors zone"} value={stats.skipped_hors_zone} />
                {google
                  ? <Stat label="Appels Google facturés" value={stats.requests} />
                  : <Stat label="Écartées : trop anciennes" value={stats.skipped_trop_anciennes} />}
              </div>
            )}
            {c.status === "error" && c.last_error && (
              <p className="mt-3 flex items-start gap-2 text-xs text-rose-600">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {c.last_error}
              </p>
            )}
          </div>

          <CampaignEmailCard campaign={c} />
        </div>
      </div>

      {editing && (
        <CampaignForm campaign={c} onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }} />
      )}
    </div>
  );
}

function Stat({ label, value, accent = "text-slate-800" }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
      <div className={`font-mono font-semibold ${accent}`}>{value ?? 0}</div>
      <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{label}</div>
    </div>
  );
}

/** Emailing de campagne — déplacé ici depuis la carte de la liste : l'envoi en
 *  masse mérite le contexte complet (destinataires éligibles, critères, résultats)
 *  plutôt qu'un bouton isolé au milieu d'une grille de cartes. */
function CampaignEmailCard({ campaign }) {
  const [objet, setObjet] = useState("");
  const [message, setMessage] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [recipients, setRecipients] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/email-templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get(`/campaigns/${campaign.id}/email-recipients`).then(({ data }) => setRecipients(data)).catch(() => {});
  }, [campaign.id]);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) { setObjet(t.objet); setMessage(t.corps); }
  };

  const send = async () => {
    if (!objet || !message) return toast.error("Objet et message requis");
    setSending(true);
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/send-email`,
        { objet, message, template_id: templateId || null });
      if (data.resend_configured === false) {
        toast.warning(`${data.queued} email(s) SIMULÉ(S) (clé Resend non configurée) · ${data.skipped_rgpd} bloqué(s) RGPD`);
      } else {
        toast.success(`${data.queued} email(s) en cours d'envoi · ${data.skipped_rgpd} bloqué(s) RGPD`);
      }
      setObjet(""); setMessage(""); setTemplateId("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSending(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="campaign-email-card">
      <h3 className="font-heading font-semibold text-slate-800 mb-1 flex items-center gap-2">
        <Mail size={17} /> Email de campagne
      </h3>
      <p className="text-xs text-slate-400 mb-3">
        {recipients ? `${recipients.count} destinataire(s) éligible(s)` : "Chargement des destinataires…"}
        {" "}— oppositions RGPD et filtre sectoriel déjà appliqués.
      </p>

      {recipients?.count === 0 ? (
        <p className="text-sm text-slate-400">
          Aucun prospect de cette campagne n'a d'adresse email exploitable.
        </p>
      ) : (
        <div className="space-y-3">
          {templates.length > 0 && (
            <select data-testid="campaign-email-template" value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
              <option value="">— Sans template —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <input data-testid="campaign-email-objet" placeholder="Objet" value={objet}
            onChange={(e) => setObjet(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <textarea data-testid="campaign-email-message" rows={6} value={message}
            placeholder={"Message…  Variables : {nom} {entreprise} {ville}"}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <button data-testid="send-campaign-email" onClick={send}
            disabled={sending || !recipients?.count}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-60">
            <Send size={15} />
            {sending ? "Envoi…" : `Envoyer à ${recipients?.count ?? "…"} prospect(s)`}
          </button>
        </div>
      )}
    </div>
  );
}
